import axios from 'axios';

import { storableError } from '../../util/errors';
import { convertUnitToSubUnit, unitDivisor } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { isOriginInUse } from '../../util/search';
import { parse } from '../../util/urlHelpers';
import { apiBaseUrl } from '../../util/api';
import {
  isAlgoliaFacetsEnabled,
  isClientSideAlgoliaConfigured,
  searchAlgoliaListingsClient,
} from '../../util/algoliaSearch';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 12 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 24;
const SEARCH_RESULTS_CACHE_LIMIT = 40;

let activeSearchAbortController = null;
let latestSearchRequestId = 0;
const searchResultsCache = new Map();
const searchResultsPromiseCache = new Map();

// ================ Action types ================ //

export const SEARCH_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_LISTINGS_REQUEST';
export const SEARCH_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_LISTINGS_SUCCESS';
export const SEARCH_LISTINGS_ERROR = 'app/SearchPage/SEARCH_LISTINGS_ERROR';

export const SEARCH_MAP_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_MAP_LISTINGS_REQUEST';
export const SEARCH_MAP_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_MAP_LISTINGS_SUCCESS';
export const SEARCH_MAP_LISTINGS_ERROR = 'app/SearchPage/SEARCH_MAP_LISTINGS_ERROR';

export const SEARCH_MAP_SET_ACTIVE_LISTING = 'app/SearchPage/SEARCH_MAP_SET_ACTIVE_LISTING';

// ================ Reducer ================ //

const initialState = {
  listings: [],
  pagination: null,
  facetCounts: {},
  searchParams: null,
  searchInProgress: false,
  searchListingsError: null,
  currentPageResultIds: [],
};

const resultIds = listings => listings.map(l => l.id);

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SEARCH_LISTINGS_REQUEST:
      return {
        ...state,
        searchParams: payload.searchParams,
        searchInProgress: true,
        searchMapListingIds: [],
        searchListingsError: null,
      };
    case SEARCH_LISTINGS_SUCCESS:
      return {
        ...state,
        listings: payload.listings,
        currentPageResultIds: resultIds(payload.listings),
        pagination: payload.pagination,
        facetCounts: payload.facetCounts || {},
        searchInProgress: false,
      };
    case SEARCH_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, searchInProgress: false, searchListingsError: payload };

    case SEARCH_MAP_SET_ACTIVE_LISTING:
      return {
        ...state,
        activeListingId: payload,
      };
    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const searchListingsRequest = searchParams => ({
  type: SEARCH_LISTINGS_REQUEST,
  payload: { searchParams },
});

export const searchListingsSuccess = ({ listings, pagination, facetCounts }) => ({
  type: SEARCH_LISTINGS_SUCCESS,
  payload: { listings, pagination, facetCounts },
});

export const searchListingsError = e => ({
  type: SEARCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

const getImageUrlFromValue = value => {
  if (typeof value === 'string') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(getImageUrlFromValue).find(Boolean) || '';
  }

  const variants = value?.attributes?.variants || value?.variants;
  return (
    value?.url ||
    variants?.['listing-card']?.url ||
    variants?.default?.url ||
    variants?.['scaled-medium']?.url ||
    ''
  );
};

const getAlgoliaHitImageUrl = hit =>
  [
    hit?.imageUrl,
    hit?.images,
    hit?.image,
    hit?.firstImage,
    hit?.publicData?.images,
    hit?.attributes?.publicData?.images,
  ]
    .map(getImageUrlFromValue)
    .find(Boolean) || '';

const createAlgoliaImage = (imageUrl, objectID) => {
  if (!imageUrl) {
    return [];
  }

  const imageId = new sdkTypes.UUID(objectID);

  return [
    {
      id: imageId,
      type: 'image',
      attributes: {
        variants: {
          default: { url: imageUrl, width: 1200, height: 1200 },
          'listing-card': { url: imageUrl, width: 800, height: 800 },
          'listing-card-2x': { url: imageUrl, width: 1200, height: 1200 },
          'scaled-small': { url: imageUrl, width: 400, height: 400 },
          'scaled-medium': { url: imageUrl, width: 800, height: 800 },
        },
      },
    },
  ];
};

const createAlgoliaAuthor = hit => {
  const authorUuid = hit.authorId || hit.objectID;

  return {
    id: new sdkTypes.UUID(authorUuid),
    type: 'user',
    attributes: {
      profile: {
        displayName: '',
        abbreviatedName: '',
      },
    },
  };
};

const createAlgoliaMoney = hit => {
  if (hit.priceAmount == null || !hit.priceCurrency) {
    return null;
  }

  return new sdkTypes.Money(hit.priceAmount, hit.priceCurrency);
};

const createAlgoliaGeolocation = hit => {
  if (hit?._geoloc?.lat == null || hit?._geoloc?.lng == null) {
    return null;
  }

  return new sdkTypes.LatLng(hit._geoloc.lat, hit._geoloc.lng);
};

const createAlgoliaListing = hit => {
  const listingUuid = new sdkTypes.UUID(hit.objectID);
  const publicData = {
    listingType: hit.listingType || '',
    transactionProcessAlias: hit.transactionProcessAlias || '',
    unitType: hit.unitType || '',
    designer: hit.designer || '',
    department: hit.department || '',
    category: hit.category || '',
    subCategory: hit.subCategory || '',
    color: hit.color || '',
    colour: hit.color || '',
    size: hit.size || '',
    condition: hit.condition || '',
    material: hit.material || '',
    otherBrand: hit.otherBrand || '',
    refrenceNumber: hit.referenceNumber || '',
  };

  return {
    id: listingUuid,
    type: 'listing',
    attributes: {
      title: hit.title || '',
      description: hit.description || '',
      geolocation: createAlgoliaGeolocation(hit),
      state: hit.state || 'published',
      createdAt: hit.createdAt ? new Date(hit.createdAt) : null,
      price: createAlgoliaMoney(hit),
      publicData,
    },
    author: createAlgoliaAuthor(hit),
    images: createAlgoliaImage(getAlgoliaHitImageUrl(hit), hit.objectID),
  };
};

const createPagination = (data, requestedPage, perPage) => ({
  page: requestedPage,
  perPage,
  totalItems: data?.nbHits || 0,
  totalPages: data?.nbPages || 1,
});

const createSearchCacheKey = params => {
  const normalizedEntries = Object.entries(params || {})
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey));

  return JSON.stringify(normalizedEntries);
};

const algoliaSearchParams = (searchParams, config) => {
  const { perPage, page = 1, price, sort, keywords, ...rest } = searchParams;
  const includeFacetCounts = isAlgoliaFacetsEnabled();
  const params = {
    ...rest,
    q: keywords || '',
    page: Math.max(Number(page) - 1, 0),
    hitsPerPage: perPage,
    includeFacetCounts,
  };

  if (price) {
    const values = price.split(',');
    if (values.length === 2) {
      params.priceMinSubunits = convertUnitToSubUnit(values[0], unitDivisor(config.currency));
      params.priceMaxSubunits = convertUnitToSubUnit(values[1], unitDivisor(config.currency));
    }
  }

  if (sort) {
    params.sort = sort;
  } else if (!sort) {
    params.sort = 'createdAt';
  }

  return params;
};

export const searchListingsWithActionCreators = (
  searchParams,
  config,
  {
    request = searchListingsRequest,
    success = searchListingsSuccess,
    error = searchListingsError,
  } = {}
) => dispatch => {
  dispatch(request(searchParams));

  const { perPage, page = 1 } = searchParams;
  const requestParams = algoliaSearchParams(searchParams, config);
  const cacheKey = createSearchCacheKey(requestParams);
  const cachedResult = searchResultsCache.get(cacheKey);

  if (cachedResult) {
    dispatch(success(cachedResult));
    return Promise.resolve(cachedResult);
  }

  const pendingResult = searchResultsPromiseCache.get(cacheKey);
  if (pendingResult) {
    return pendingResult;
  }

  const requestId = latestSearchRequestId + 1;
  latestSearchRequestId = requestId;

  if (activeSearchAbortController) {
    activeSearchAbortController.abort();
  }

  activeSearchAbortController = new AbortController();

  const pendingRequest = Promise.resolve()
    .then(() =>
      isClientSideAlgoliaConfigured()
        ? searchAlgoliaListingsClient({
            ...requestParams,
            query: requestParams.q,
            signal: activeSearchAbortController.signal,
          }).then(data => ({ data }))
        : axios.get(
            `${apiBaseUrl(process.env.REACT_APP_MARKETPLACE_ROOT_URL)}/api/algolia/search`,
            {
              params: requestParams,
              signal: activeSearchAbortController.signal,
              withCredentials: true,
            }
          )
    )
    .then(response => {
      if (requestId !== latestSearchRequestId) {
        return null;
      }

      const listings = (response.data?.hits || []).map(createAlgoliaListing);
      const pagination = createPagination(response.data, Number(page), perPage);
      const facetCounts = response.data?.facetCounts || {};
      const nextResult = { listings, pagination, facetCounts };

      searchResultsCache.set(cacheKey, nextResult);
      if (searchResultsCache.size > SEARCH_RESULTS_CACHE_LIMIT) {
        const oldestKey = searchResultsCache.keys().next().value;
        searchResultsCache.delete(oldestKey);
      }

      dispatch(success(nextResult));
      return nextResult;
    })
    .catch(e => {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') {
        return null;
      }

      dispatch(error(storableError(e)));
      throw e;
    })
    .finally(() => {
      searchResultsPromiseCache.delete(cacheKey);
    });

  searchResultsPromiseCache.set(cacheKey, pendingRequest);
  return pendingRequest;
};

export const searchListings = (searchParams, config) =>
  searchListingsWithActionCreators(searchParams, config);

export const setActiveListing = listingId => ({
  type: SEARCH_MAP_SET_ACTIVE_LISTING,
  payload: listingId,
});

export const loadData = (params, search, config) => {
  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  const { page = 1, origin, pub_department, fromSearch, ...rest } = queryParams;
  const originMaybe = isOriginInUse(config) && origin ? { origin } : {};
  const department = pub_department || 'women';

  return searchListings(
    {
      ...rest,
      ...originMaybe,
      page,
      pub_department: department,
      perPage: RESULT_PAGE_SIZE,
    },
    config
  );
};
